import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { XMarkIcon, UserPlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface UserOption {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
}

interface ShareEntry {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email?: string;
  permission: 'read' | 'write';
}

interface ElternbriefShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  elternbriefId: number | null;
  currentUserId?: number;
  onUpdated: () => void;
}

const permissionLabels = {
  read: 'Nur Lesen',
  write: 'Schreiben'
};

const ElternbriefShareModal: React.FC<ElternbriefShareModalProps> = ({
  isOpen,
  onClose,
  elternbriefId,
  currentUserId,
  onUpdated
}) => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [shares, setShares] = useState<ShareEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [permission, setPermission] = useState<'read' | 'write'>('read');

  useEffect(() => {
    if (!isOpen || !elternbriefId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const [usersResponse, sharesResponse] = await Promise.all([
          api.get('/users/simple'),
          api.get(`/massenmail/${elternbriefId}/shares`)
        ]);
        setUsers(usersResponse.data || []);
        if (sharesResponse.data?.success) {
          setShares(sharesResponse.data.data || []);
        }
      } catch (error) {
        console.error('Error loading share data:', error);
        toast.error('Fehler beim Laden der Freigaben');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, elternbriefId]);

  const availableUsers = users.filter(user => {
    const role = (user.role || '').toString().toLowerCase();
    if (user.id === currentUserId) return false;
    if (role === 'student' || role === 'students') return false;
    return true;
  });

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (!elternbriefId || selectedUserIds.length === 0) {
      toast.error('Bitte mindestens eine Person auswählen');
      return;
    }

    try {
      const response = await api.post(`/massenmail/${elternbriefId}/shares`, {
        userIds: selectedUserIds,
        permission
      });
      if (response.data?.success) {
        toast.success('Elternbrief geteilt');
        setSelectedUserIds([]);
        const sharesResponse = await api.get(`/massenmail/${elternbriefId}/shares`);
        if (sharesResponse.data?.success) {
          setShares(sharesResponse.data.data || []);
        }
        onUpdated();
      }
    } catch (error: any) {
      console.error('Error sharing Elternbrief:', error);
      toast.error(error?.response?.data?.error || 'Fehler beim Teilen');
    }
  };

  const handleRemoveShare = async (userId: number) => {
    if (!elternbriefId) return;
    try {
      await api.delete(`/massenmail/${elternbriefId}/shares/${userId}`);
      setShares(prev => prev.filter(entry => entry.user_id !== userId));
      onUpdated();
      toast.success('Freigabe entfernt');
    } catch (error: any) {
      console.error('Error removing share:', error);
      toast.error(error?.response?.data?.error || 'Fehler beim Entfernen');
    }
  };

  if (!isOpen || !elternbriefId) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <UserPlusIcon className="h-6 w-6 text-indigo-600" />
                <h3 className="text-lg font-medium text-gray-900">Elternbrief teilen</h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="mt-2 text-gray-600">Laden...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Berechtigung</label>
                  <div className="flex gap-4">
                    {(['read', 'write'] as const).map(value => (
                      <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="radio"
                          name="permission"
                          value={value}
                          checked={permission === value}
                          onChange={() => setPermission(value)}
                          className="text-indigo-600 focus:ring-indigo-500"
                        />
                        {permissionLabels[value]}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lehrer auswählen</label>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {availableUsers.map(user => (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-gray-500">{user.username}</div>
                        </div>
                      </label>
                    ))}
                    {availableUsers.length === 0 && (
                      <div className="px-3 py-4 text-sm text-gray-500">Keine weiteren Nutzer verfügbar</div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Teilen
                  </button>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Aktive Freigaben</h4>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {shares.map(share => (
                      <div key={share.user_id} className="flex items-center justify-between px-3 py-2">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {share.first_name} {share.last_name}
                          </div>
                          <div className="text-xs text-gray-500">{share.username}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                            {permissionLabels[share.permission]}
                          </span>
                          <button
                            onClick={() => handleRemoveShare(share.user_id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Freigabe entfernen"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {shares.length === 0 && (
                      <div className="px-3 py-4 text-sm text-gray-500">Noch keine Freigaben</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElternbriefShareModal;
